import React, { useCallback, useEffect, useId, useState } from 'react';

import Datepicker from 'tailwind-datepicker-react';

import { twMerge } from 'tailwind-merge';

import type { Dayjs } from 'dayjs';

import dayjs from 'dayjs';

import { TbArrowLeft, TbArrowRight, TbCalendar } from 'react-icons/tb';

import Icon from 'components-new/icon/icon';
import Typography from 'components-new/typography/typography';

import { createLocale } from 'locales';

const globalLocalize = createLocale('global');

export type DateInputProps = {
  title?: string;
  placeholder?: string;
  className?: string;
  defaultValue?: Dayjs;
  onChange?: (value?: Dayjs) => void;
  disabled?: boolean;
  disabledDates?: Date[];
  inputClassName?: string;
  error?: string;
  errorClassName?: string;
  maxDate?: Date;
  minDate?: Date;
};

const DateInput: React.FC<DateInputProps> = ({
  title,
  placeholder,
  className,
  defaultValue,
  onChange,
  disabled,
  disabledDates,
  inputClassName,
  error,
  errorClassName,
  maxDate,
  minDate,
}) => {
  const [date, setDate] = useState<Dayjs | ''>(defaultValue ?? '');
  const [show, setShow] = useState<boolean>(false);

  const id = useId();

  const handleChange = useCallback(
    (dt: Date) => {
      const dayjsDate = dayjs(dt);
      onChange?.(dayjsDate);
      setDate(dayjsDate);
    },
    [onChange],
  );

  useEffect(() => {
    // necessary due to autoHide bug: https://github.com/OMikkel/tailwind-datepicker-react/issues/26#issuecomment-1657978337

    window.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;

      const calendarShouldStayActive =
        document.getElementById(id) === target ||
        target.closest('.datepickerContainer')?.firstElementChild?.id === id;

      if (calendarShouldStayActive) {
        return;
      }
      setShow(false);
    });
  }, [id]);

  const options: React.ComponentProps<typeof Datepicker>['options'] = {
    // autoHide: true, // bug -> this doesn't work due to having a custom input component as child.
    clearBtn: true,
    clearBtnText: globalLocalize('clear'),
    datepickerClassNames: 'top-8',
    defaultDate: new Date(),
    disabledDates: disabledDates || [],
    icons: {
      next: () => <Icon icon={TbArrowRight} />,
      prev: () => <Icon icon={TbArrowLeft} />,
    },
    language: 'en',
    maxDate: maxDate || new Date('2030-01-01'),
    minDate: minDate || new Date('1950-01-01'),
    theme: {
      background: 'bg-white dark:bg-white',
      clearBtn:
        'text-gray600 dark:text-gray600 hover:text-gray800 dark:hover:text-gray800 bg-background dark:bg-background hover:bg-background-hover dark:hover:bg-background-hover border-none',
      disabledText:
        'bg-white dark:bg-white text-gray100 dark:text-gray100 hover:bg-destructive-100 dark:hover:bg-destructive-100',
      icons:
        'text-gray600 dark:text-gray600 hover:text-gray800 dark:hover:text-gray800 bg-white dark:bg-white hover:bg-background-hover dark:hover:bg-background-hover',
      input: '',
      inputIcon: '',
      selected: 'bg-primary-300 text-white dark:text-white',
      text: 'text-gray800 dark:text-gray800 hover:bg-primary-100 dark:hover:bg-primary-100',
      todayBtn: '',
    },
    title: '',
    todayBtn: false,
    weekDays: ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'],
  };

  const notDisabledAndErrorClassName = `border-transparent active:border-primary active:outline active:outline-primary-200 active:bg-input-active
       focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1 focus:ring-offset-primary-200
       focus-within:ring-1 focus-within:ring-primary-200 focus-within:bg-input-active
       focus-within:border-primary`;

  const inputClassNames = twMerge(
    'block w-full p-2 pl-8',
    'outline-none bg-input-idle rounded',
    'text-sm font-body font-light',
    'transition ease-in-out duration-200',
    'border',
    disabled ? 'placeholder-gray-300' : 'hover:bg-input-hover',
    error &&
      `border border-destructive focus:border-none focus:ring-1 focus:ring-destructive-200 focus:ring-offset-1 focus:ring-offset-destructive-200
      focus-within:ring-2 focus-within:ring-destructive-200 focus-within:bg-input-idle`,

    !disabled && !error && notDisabledAndErrorClassName,
    inputClassName,
  );

  const iconClassName = twMerge(
    'absolute left-2',
    disabled ? 'text-gray-300' : 'text-gray-500',
  );

  const errorClassnames = twMerge('text-destructive mt-1', errorClassName);

  return (
    <div className={twMerge(className)}>
      {title && (
        <Typography as="p" variant="body-medium-bold" className="mb-2">
          {title}
        </Typography>
      )}
      <div className="relative flex items-center">
        <Icon icon={TbCalendar} className={iconClassName} size={18} />

        <Datepicker
          options={options}
          onChange={handleChange}
          show={show}
          setShow={setShow}
          classNames="datepickerContainer"
        >
          <input
            id={id}
            type="text"
            className={inputClassNames}
            value={date ? date?.format('DD/MM/YYYY') : ''}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => setShow(true)}
            readOnly
          />
        </Datepicker>
      </div>
      {error && (
        <Typography as="p" variant="body-medium" className={errorClassnames}>
          {error}
        </Typography>
      )}
    </div>
  );
};

export default DateInput;
